/**
 * Two blocks of the sign-up form (redesign §37.3, §37.4): the account kind as
 * a full-width segmented control with the chosen option's description under
 * it and the Organization name field that opens beneath, and the invitation's
 * address as a fixed-value field.
 */
import type React from 'react';
import { color } from '../theme/tokens';
import { Segmented } from '../components/FilterBar';
import { LockMark } from '../components/icons';
import { Input } from '../components/primitives';
import type { AccountType } from '../lib/types';

const LABEL: React.CSSProperties = { display: 'block', fontSize: 13, lineHeight: 1.4, fontWeight: 500, color: color.ink, marginBottom: 6 };
const HELP: React.CSSProperties = { fontSize: 13, lineHeight: 1.5, color: color.inkSubtle, margin: '6px 0 0' };

const KINDS: Record<AccountType, string> = {
  personal: 'One licence, one person.',
  org: 'Seats, invites, one invoice.'
};

/**
 * Personal / Organization (§37.4): the segmented L at full width, the option's
 * description crossfading under it in 120 ms, and Organization name opening
 * below in height and opacity on a spring-like curve (d 1 r .3), closing in
 * 120 ms; closed, it is inert.
 */
export function AccountKind({
  value,
  onChange,
  organizationName,
  onOrganizationName,
  inputStyle
}: {
  value: AccountType;
  onChange: (kind: AccountType) => void;
  organizationName: string;
  onOrganizationName: (name: string) => void;
  inputStyle: React.CSSProperties;
}): React.JSX.Element {
  const open = value === 'org';
  return (
    <div style={{ marginBottom: 20 }}>
      <Segmented
        full
        label="Account type"
        value={value}
        onChange={onChange}
        options={[
          { id: 'personal', label: 'Personal' },
          { id: 'org', label: 'Organization' }
        ]}
      />
      <p key={value} className="al-fade-in" style={HELP}>
        {KINDS[value]}
      </p>
      <div
        inert={!open}
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
          transition: open
            ? 'grid-template-rows 320ms cubic-bezier(.2,.9,.3,1), opacity 200ms ease'
            : 'grid-template-rows 120ms ease, opacity 120ms ease'
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div
            style={{
              paddingTop: 20,
              transform: open ? 'none' : 'translateY(-4px)',
              transition: open ? 'transform 320ms cubic-bezier(.2,.9,.3,1)' : 'transform 120ms ease'
            }}
          >
            <label htmlFor="auth-org" style={LABEL}>
              Organization name
            </label>
            <Input
              id="auth-org"
              required={open}
              aria-required={open || undefined}
              autoComplete="organization"
              value={organizationName}
              onChange={(event) => onOrganizationName(event.target.value)}
              placeholder="Acme Engineering"
              style={inputStyle}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A value that cannot change and has no reason to be copied (§37.3): the
 * field block without an input's border — a 1 px hairline frame, radius 8, 36
 * high, the value in 13.5 ink and a lock at the right — with its help under it.
 */
export function FixedField({ id, label, value, help }: { id: string; label: string; value: string; help: string }): React.JSX.Element {
  return (
    <div>
      <label htmlFor={id} style={LABEL}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          readOnly
          value={value}
          aria-describedby={`${id}-help`}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: 36,
            padding: '0 36px 0 12px',
            border: `1px solid ${color.line}`,
            borderRadius: 8,
            background: color.surface,
            fontFamily: 'inherit',
            fontSize: 13.5,
            color: color.ink,
            textOverflow: 'ellipsis'
          }}
        />
        <span style={{ position: 'absolute', right: 12, top: 10, color: color.inkSubtle, pointerEvents: 'none' }}>
          <LockMark size={16} />
        </span>
      </div>
      <p id={`${id}-help`} style={HELP}>
        {help}
      </p>
    </div>
  );
}
